import {
	ColumnDef,
	ColumnFilter,
	ColumnFiltersState,
	RowSelectionState,
	SortingState,
} from '@tanstack/react-table';
import { useGetNotifications } from 'api/hooks/useGetNotifications';
import { useGetProjects } from 'api/hooks/useGetProjects';
import { IProject } from 'api/resources/projects';
import Table from 'legacy/app/components/newTable/Table';
import {
	SECURITY_ATTRIBUTE_TYPES,
	useHasAccess,
} from 'legacy/app/context/security';
import TableCheckboxCell from 'legacy/app/components/newTable/partials/TableCheckboxCell';
import URI from 'legacy/defaults/RoutesDefault';
import { formatDate, formatFilterDate } from 'legacy/helpers/Date';
import { currencyFormat } from 'legacy/helpers/Number';
import { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { generatePath, Link, LinkProps } from 'react-router-dom';
import { Pagination } from 'legacy/app/components/pagination/Pagination';
import { DateRange } from 'rsuite/esm/DateRangePicker';
import { getCookie } from 'legacy/utilities/Auth';
import { setLocalStorage } from 'legacy/utilities/LocalStorage';
import { EmptyProjectListState } from './EmptyProjectListState';
import { useGetRetainerPDF } from 'api/hooks/useGetRetainerPDF';
import {
	InputWithNumbersFilterModal,
	TNumberFilter,
} from './InputWithNumbersFilterModal';

type TCustomBootstrapLinkType = React.ComponentClass<LinkProps> &
	keyof JSX.IntrinsicElements;

const instanceId = getCookie('dmInstanceId');

export const ProjectTable = ({
	sendRowSelection,
	enableFilters,
	clientFilter,
	managerFilter,
	statusFilter,
}: {
	clientFilter?: string;
	managerFilter?: string;
	statusFilter?: string;
	enableFilters: boolean;
	sendRowSelection: (rowSelection: RowSelectionState, ids: string[]) => void;
}) => {
	const existingFilters = localStorage.getItem(
		`${instanceId}-projects-list-filter`
	);
	const existingSort = localStorage.getItem(`${instanceId}-projects-list-sort`);
	const [pageSize, setPageSize] = useState(20);
	const [page, setPage] = useState(1);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
		existingFilters
			? JSON.parse(
					localStorage.getItem(`${instanceId}-projects-list-filter`) as string
			  )
					.filter((filter: ColumnFilter) => filter.id !== 'enableFilters')
					.map((filter: ColumnFilter) =>
						filter.id !== 'dateopened' && filter.id !== 'closeddt'
							? filter
							: {
									...filter,
									value: (filter.value as DateRange).map(
										(value) => new Date(value)
									),
							  }
					)
			: []
	);
	const [sorting, setSorting] = useState<SortingState>(
		existingSort && JSON.parse(existingSort).length
			? JSON.parse(existingSort)
			: [{ id: 'proj', desc: false }]
	);
	const [showEmptyState, setShowEmptyState] = useState(false);
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
	const canSeeBudget = useHasAccess(200, SECURITY_ATTRIBUTE_TYPES.DenyAccess);
	const { mutateAsync: getRetainerPDF } = useGetRetainerPDF();
	const [pdfRetainerActiveFetches, setPDFRetainerActiveFetches] = useState<{
		[key: number]: boolean;
	}>({});

	const menuFilters = useMemo(
		() => [
			{
				type: 'client',
				value: clientFilter,
			},
			{
				type: 'manager',
				value: managerFilter,
			},
			{
				type: 'status',
				value: statusFilter,
			},
		],
		[clientFilter, managerFilter, statusFilter]
	);

	const haveMenuFiltersValue = useMemo(
		() => menuFilters.find((filter) => filter.value),
		[menuFilters]
	);

	const projectsFilter = useMemo(() => {
		let filterString = `?$top=${pageSize + 1}&$skip=${
			(page - 1) * pageSize
		}`.trim();

		menuFilters.map((filter) => {
			if (filter.type === 'client' && filter.value) {
				filterString = `${filterString} and client eq '${encodeURIComponent(
					(filter.value as string).replace(/'/g, "''")
				)}'`;
			} else if (filter.type === 'manager' && filter.value) {
				filterString = `${filterString} and manager eq '${encodeURIComponent(
					(filter.value as string).replace(/'/g, "''")
				)}'`;
			} else if (filter.type === 'status' && filter.value) {
				filterString = `${filterString} and closeddt ${
					filter.value === 'open' ? 'eq' : 'ne'
				} null`;
			}
		});

		if (enableFilters) {
			setLocalStorage(
				`${instanceId}-projects-list-filter`,
				JSON.stringify([...columnFilters, { id: 'enableFilters', value: true }])
			);

			columnFilters.map((filter) => {
				if (
					(filter.id === 'dateopened' && filter.value) ||
					(filter.id === 'closeddt' && filter.value)
				) {
					const startDate = formatFilterDate((filter.value as DateRange)[0]);
					const endDate = formatFilterDate((filter.value as DateRange)[1]);
					filterString = `${filterString} and ${filter.id} ge ${startDate} and ${filter.id} le ${endDate}`;
					return;
				} else if (
					filter.id === 'budgett' ||
					filter.id === 'requestedRetainerAmount'
				) {
					const filterValue = filter.value as TNumberFilter['value'];
					if (filterValue.min) {
						filterString = `${filterString} and ${filter.id} le ${filterValue.min}`;
					}
					if (filterValue.max) {
						filterString = `${filterString} and ${filter.id} ge ${filterValue.max}`;
					}

					return;
				}
				filterString = `${filterString} and contains(${
					filter.id
				}, '${encodeURIComponent(
					(filter.value as string).replace(/'/g, "''")
				)}')`;
			});
		}

		if (!columnFilters.length || !enableFilters) {
			localStorage.removeItem(`${instanceId}-projects-list-filter`);
		}

		sorting.map((sort) => {
			filterString = `${filterString} &$orderby=${sort.id} ${
				sort.desc ? 'desc' : 'asc'
			}`;
			return;
		});

		localStorage.setItem(
			`${instanceId}-projects-list-sort`,
			JSON.stringify(sorting)
		);

		if (columnFilters.length > 0 || haveMenuFiltersValue) {
			// If there are filters, remove the first 'and' from the filter string so that the query doesn´t break
			filterString = filterString.replace('and', '&$filter=');
		}

		return filterString;
	}, [
		pageSize,
		page,
		menuFilters,
		enableFilters,
		sorting,
		columnFilters,
		haveMenuFiltersValue,
	]);

	const { isLoading: isLoadingProjects, data: projects } = useGetProjects(
		projectsFilter,
		{ enabled: !showEmptyState }
	);

	const slicedProjects = useMemo(
		() => (projects ? projects.slice(0, pageSize) : []),
		[projects, pageSize]
	);

	const notificationFilter = useMemo(() => {
		if (projects?.length) {
			const filteredProjects = projects
				.filter((project) => project.lastRetainerRequestNotificationId !== null)
				.map((project) => project.lastRetainerRequestNotificationId);

			if (filteredProjects.length > 0) {
				return `?$filter=id in (${filteredProjects.join()})`;
			}

			return null;
		}
	}, [projects]);

	const {
		data: notifications,
		isLoading: isLoadingNotifications,
		fetchStatus,
	} = useGetNotifications(notificationFilter || '', {
		enabled: !!notificationFilter && !showEmptyState,
	});

	const isFetchingNotifications =
		isLoadingNotifications && fetchStatus === 'fetching';

	useEffect(() => {
		if (projects) {
			const ids = projects
				.filter((project) => rowSelection[project.id])
				.map((project) => project.proj);
			sendRowSelection(rowSelection, ids);
		}
	}, [sendRowSelection, rowSelection, projects]);

	useEffect(() => {
		if (
			!isLoadingProjects &&
			!projects?.length &&
			!columnFilters.length &&
			!haveMenuFiltersValue
		) {
			setShowEmptyState(true);
		}
	}, [columnFilters, haveMenuFiltersValue, projects, isLoadingProjects]);

	useEffect(() => {
		setPage(1);
	}, [columnFilters, menuFilters]);

	useEffect(() => {
		setRowSelection({});
	}, [projects]);

	const filteredColumns = useMemo(() => {
		const columns: ColumnDef<IProject>[] = [
			{
				id: 'select',
				size: 50,
				header: ({ table }) =>
					TableCheckboxCell({
						table,
						disabled: false,
					}),
				cell: ({ row }) =>
					TableCheckboxCell({
						'data-testid': `projects-table-row-${row?.index}-check`,
						table: undefined,
						row,
						disabled: false,
					}),
			},
			{
				accessorKey: 'proj',
				header: 'Project Code',
				meta: {
					inputTestId: 'search-project-code-input',
				},
				// eslint-disable-next-line react/no-unstable-nested-components
				cell: (info) => (
					<div>
						<Link
							to={`/projects/${info.row.original.id}/items`}
							className="text-charcoal hover-view-icon"
							data-testid={`projects-table-row-${info.row.index}-code`}
						>
							{info.row.original.proj}
						</Link>
						{info.row.original.numAttachments > 0 && (
							<Button
								as={Link as unknown as TCustomBootstrapLinkType}
								to={generatePath(URI.project.viewPage, {
									id: info.row.original.id,
									page: 'documents',
								})}
								variant="ivory"
								size="sm"
								className="border-0 fsx-16 ri-attachment-2 square ms-1"
								style={{
									textDecoration: 'none',
								}}
							/>
						)}
					</div>
				),
			},
			{
				accessorKey: 'projn',
				header: 'Project Name',
				// eslint-disable-next-line react/no-unstable-nested-components
				cell: (info) => (
					<Link
						to={`/projects/${info.row.original.id}/items`}
						className="text-charcoal hover-view-icon"
					>
						{info.getValue() as string}
					</Link>
				),
			},
			{
				accessorKey: 'client',
				header: 'Client Code',
				cell: (info) => info.getValue(),
			},
			{
				accessorKey: 'clientName',
				header: 'Client Name',
				cell: (info) => info.getValue(),
				sortDescFirst: false,
			},
			{
				accessorKey: 'manager',
				header: 'Salesperson Code',
				cell: (info) => info.getValue(),
				sortDescFirst: false,
			},
			{
				accessorKey: 'dateopened',
				size: enableFilters ? 300 : 150,
				meta: {
					CustomFilterInputType: 'date',
				},
				header: 'Date Opened',
				accessorFn: (row) => (row.dateopened ? formatDate(row.dateopened) : ''),
				cell: (info) => info.getValue(),
			},
			{
				accessorKey: 'closeddt',
				size: enableFilters ? 300 : 150,
				meta: {
					CustomFilterInputType: 'date',
				},
				cell: (info) => info.getValue(),
				accessorFn: (row) => (row.closeddt ? formatDate(row.closeddt) : ''),
				header: 'Date Closed',
			},
			{
				accessorKey: 'clientorder',
				header: 'Client Order No.',
				cell: (info) => info.getValue(),
			},
			{
				accessorKey: 'budgett',
				header: 'Budget',
				accessorFn: (row) => currencyFormat(row.budgett ?? 0, '$'),
				meta: {
					customInput: (
						<InputWithNumbersFilterModal
							onChange={(val) =>
								setColumnFilters((prev) =>
									prev.filter((filter) => filter.id !== 'budgett').concat(val)
								)
							}
							title="Budget Range"
							name="budgett"
						/>
					),
				},
				cell: (info) => info.getValue(),
			},
			{
				accessorKey: 'requestedRetainerAmount',
				header: 'Requested Retainer',
				size: 200,
				sortDescFirst: false,
				// eslint-disable-next-line react/no-unstable-nested-components
				meta: {
					customInput: (
						<InputWithNumbersFilterModal
							onChange={(val) =>
								setColumnFilters((prev) =>
									prev
										.filter((filter) => filter.id !== 'requestedRetainerAmount')
										.concat(val)
								)
							}
							title="Requested Retainer Amount Range"
							name="requestedRetainerAmount"
						/>
					),
				},
				// eslint-disable-next-line react/no-unstable-nested-components
				accessorFn: (row) => (
					<span
						className="tw-cursor-pointer"
						onClick={async () => {
							if (pdfRetainerActiveFetches[row.id]) {
								return;
							}
							setPDFRetainerActiveFetches((prev) => ({
								...prev,
								[row.id]: true,
							}));

							try {
								const document = await getRetainerPDF({
									objectId: row.id,
								});
								const newWindow = window?.open?.(document, '_blank');
								if (newWindow) {
									newWindow.focus();
								}
							} catch (error) {
								console.error('Error generating PDF:', error);
							} finally {
								setPDFRetainerActiveFetches((prev) => ({
									...prev,
									[row.id]: false,
								}));
							}
						}}
					>
						{currencyFormat(row.requestedRetainerAmount, '$')}
					</span>
				),
				cell: (info) => info.getValue(),
			},
			{
				accessorKey: 'lastRetainerRequestNotificationId',
				// eslint-disable-next-line react/no-unstable-nested-components
				cell: (info) => {
					const notification = notifications?.find(
						(notification) =>
							notification.id ===
							info.row.original.lastRetainerRequestNotificationId
					);
					if (notification) {
						return (
							<div className="tw-flex tw-flex-col">
								<span>
									{formatDate(notification.sentDateTimeInUserTimezone)}
								</span>
								<span>{notification.sentInfo}</span>
							</div>
						);
					}
				},
				enableSorting: false,
				enableColumnFilter: false,
				// eslint-disable-next-line react/no-unstable-nested-components
				header: () => (
					<div className={enableFilters ? 'tw-pb-1' : ''}>
						Requested Retainer Sent
					</div>
				),
			},
		];

		return columns.filter((col) =>
			canSeeBudget ? col : col.header !== 'Budget'
		);
	}, [
		canSeeBudget,
		notifications,
		getRetainerPDF,
		pdfRetainerActiveFetches,
		enableFilters,
	]);

	return showEmptyState ? (
		<EmptyProjectListState />
	) : (
		<div>
			<div className="tw-max-h-[600px] tw-w-full tw-overflow-y-auto">
				<div className="tw-p-4" id="projects_table">
					<Table
						data-testid="projects-table"
						state={{
							rowSelection,
							columnFilters,
							sorting,
						}}
						manualFiltering
						filtersTestId="search-fields-table"
						manualSorting
						onSortingChange={setSorting}
						onColumnFiltersChange={setColumnFilters}
						enableFilters={enableFilters}
						onRowSelectionChange={(state) => setRowSelection(state)}
						getRowId={(row) => String(row.id)}
						columns={filteredColumns}
						isLoading={isLoadingProjects || isFetchingNotifications}
						data={slicedProjects}
					/>
				</div>
			</div>
			{projects && projects.length > 0 && (
				<div className="tw-mt-4">
					<Pagination
						onPageSizeChanged={setPageSize}
						onPageChanged={(newPage) => {
							setPage(newPage);
							setRowSelection({});
						}}
						hasPreviousPage={
							page > 1 ||
							((isFetchingNotifications || isLoadingProjects) && false)
						}
						hasNextPage={
							projects.length > pageSize ||
							((isFetchingNotifications || isLoadingProjects) && false) ||
							false
						}
						page={page}
						pageSize={pageSize}
					/>
				</div>
			)}
		</div>
	);
};
ProjectTable.displayName = 'ProjectTable';
